import React from 'react';
import './App.css';
import { RouterProvider } from 'react-router-dom';
import router from './router';
import { Authenticator } from '@aws-amplify/ui-react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SnackbarProvider, MaterialDesignContent, closeSnackbar } from 'notistack';
import { BasketProvider } from './redux/BasketContext';
import { TutorialProvider } from './components/ui/organisms/TutorialContext';
import { Box, Button, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { styled, alpha } from '@mui/material/styles';
import RumErrorBoundary from './components/RumErrorBoundary';
import Loading from './components/layout/Loading';
import SuperchatWidgetMount from './components/SuperchatWidgetMount';
import { setRumConsent, shouldShowRumConsentBanner } from './rum';
import { cssBackgroundFlag } from './utils';

const StyledSnackbarContent = styled(MaterialDesignContent)(({ theme }) => {
	const variant = (main: string) => {
		const contrast = theme.palette.getContrastText(main);
		return {
			backgroundColor: main,
			color: contrast,
			'& #notistack-snackbar svg': {
				color: contrast,
				backgroundColor: alpha(theme.palette.common.white, 0.2),
				borderRadius: '50%',
				padding: 3,
				boxSizing: 'content-box',
				marginInlineEnd: theme.spacing(0.5)
			},
			'& button': { color: contrast }
		};
	};
	return {
		'&.notistack-MuiContent': {
			borderRadius: 12,
			padding: theme.spacing(0.5, 2),
			minWidth: 320,
			fontSize: '0.875rem',
			fontWeight: 600,
			letterSpacing: 0.1,
			boxShadow: theme.shadows[6],
			'& #notistack-snackbar': { gap: theme.spacing(1), paddingBlock: theme.spacing(0.75) }
		},
		'&.notistack-MuiContent-success': variant(theme.palette.success.main),
		'&.notistack-MuiContent-error': variant(theme.palette.error.main),
		'&.notistack-MuiContent-warning': variant(theme.palette.warning.main),
		'&.notistack-MuiContent-info': variant(theme.palette.info.main),
		'&.notistack-MuiContent-default': variant(theme.palette.grey[800])
	};
});

function AppBackground() {
	const theme = useTheme();
	const [pathname, setPathname] = React.useState(router.state.location.pathname);
	React.useEffect(() => {
		const unsubscribe = router.subscribe((state) => setPathname(state.location.pathname));
		return unsubscribe;
	}, []);
	// Animate the canvas throughout the login / onboarding flow (sign in, MFA setup, plan).
	const isLogin = pathname.startsWith('/login');

	if (cssBackgroundFlag) {
		return (
			<div
				className={`bg-css-rainbow${isLogin ? ' bg-css-rainbow--animated' : ''}`}
				data-theme={theme.palette.mode}
			/>
		);
	}

	return (
		<Box
			sx={{
				position: 'fixed',
				inset: 0,
				backgroundImage: `url(${process.env.PUBLIC_URL}/circles.png), url(${process.env.PUBLIC_URL}/bg-rainbow.jpg)`,
				backgroundRepeat: 'no-repeat, no-repeat',
				backgroundPosition: 'top, top',
				backgroundSize: 'contain, cover',
				zIndex: -1
			}}
		>
			{theme.palette.mode === 'dark' && (
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						backgroundColor: 'rgba(0,0,0,0.65)',
						pointerEvents: 'none'
					}}
				/>
			)}
		</Box>
	);
}

function App() {
	const [showConsentBanner, setShowConsentBanner] = React.useState<boolean>(() => shouldShowRumConsentBanner());

	const handleConsent = (consent: 'accepted' | 'rejected') => {
		const saved = setRumConsent(consent);
		setShowConsentBanner(false);

		// Only reload when enabling cookies AND consent was persisted.
		// If localStorage is blocked, saved=false prevents a reload loop
		// where the banner reappears every time because consent was never written.
		if (consent === 'accepted' && saved) {
			window.location.reload();
		}
	};

	return (
		<RumErrorBoundary>
			<Authenticator.Provider>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<SnackbarProvider
						maxSnack={3}
						autoHideDuration={4000}
						preventDuplicate
						disableWindowBlurListener
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						iconVariant={{ error: <ReportProblemRoundedIcon fontSize="small" /> }}
						action={(snackbarId) => (
							<IconButton
								size="small"
								aria-label="Dismiss notification"
								onClick={() => closeSnackbar(snackbarId)}
								sx={{ color: 'inherit', opacity: 0.8, '&:hover': { opacity: 1 } }}
							>
								<CloseRoundedIcon fontSize="small" />
							</IconButton>
						)}
						Components={{
							default: StyledSnackbarContent,
							success: StyledSnackbarContent,
							error: StyledSnackbarContent,
							warning: StyledSnackbarContent,
							info: StyledSnackbarContent
						}}
					>
						<BasketProvider>
							<TutorialProvider>
								<AppBackground />
								<React.Suspense fallback={<Loading />}>
									<RouterProvider router={router} />
								</React.Suspense>
								{/* Above the router on purpose: /login, /error and the MFA
								    redirect never mount a protected layout, and those are
								    exactly the screens Ops asked for the widget on. */}
								<SuperchatWidgetMount />
								{showConsentBanner && (
									<Paper
										elevation={8}
										role="region"
										aria-label="Cookie preferences"
										sx={{
											position: 'fixed',
											left: 16,
											right: 16,
											bottom: 16,
											// Above the drawer and app bar, but BELOW the modal layer
											// (theme.zIndex.modal is 1300). At the previous literal 1400
											// this banner outranked every dialog in the app and
											// intercepted their pointer events: an open dialog's buttons
											// resolved, reported "visible, enabled and stable", and then
											// the click was swallowed by the banner sitting on top.
											//
											// Worse for a real user than for the suite that found it: MUI
											// sets aria-hidden on #root while a dialog is open, so the
											// banner was simultaneously invisible to assistive technology
											// and still capturing clicks.
											zIndex: (theme) => theme.zIndex.drawer + 1,
											p: 2,
											borderRadius: 2
										}}
									>
										<Stack
											direction={{ xs: 'column', sm: 'row' }}
											spacing={1.5}
											alignItems={{ xs: 'flex-start', sm: 'center' }}
										>
											<Box sx={{ flex: 1 }}>
												<Typography variant="subtitle2" fontWeight={700}>
													Cookie Preferences
												</Typography>
												<Typography variant="body2" color="text.secondary">
													We use optional performance monitoring cookies to improve
													reliability and diagnose issues. Rejecting disables these cookies;
													all core features remain available.
												</Typography>
											</Box>
											<Stack direction="row" spacing={1}>
												<Button
													size="small"
													variant="outlined"
													onClick={() => handleConsent('rejected')}
												>
													Reject Cookies
												</Button>
												<Button
													size="small"
													variant="contained"
													onClick={() => handleConsent('accepted')}
												>
													Accept Cookies
												</Button>
											</Stack>
										</Stack>
									</Paper>
								)}
							</TutorialProvider>
						</BasketProvider>
					</SnackbarProvider>
				</LocalizationProvider>
			</Authenticator.Provider>
		</RumErrorBoundary>
	);
}

export default App;
